import React, { useEffect, useRef, useState } from 'react';
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../api/client';
import { drawBboxLabelsOnCanvas } from './dvc/bboxOverlay';
import type { GroundTruthLabel } from './dvc/types';
import { Button, Card, EmptyState, tokens } from './ui/controls';

const CHART_COLORS = ['#007bff', '#1e7e34', '#a15c00', '#b3261e', '#0b5ed7', '#455a64', '#6f42c1', '#0f766e'];

type CountRow = { value: string; count: number };

export type DryRunStatistics = {
  rows: number;
  images: number;
  tags: CountRow[];
  robot_types: CountRow[];
  robot_names: CountRow[];
  img_widths: CountRow[];
  img_heights: CountRow[];
  species: CountRow[];
};

export type DryRunPreviewData = {
  dataset_id?: string;
  num_records?: number;
  columns?: string[];
  preview?: Array<Record<string, string>>;
  csv_text?: string;
  statistics?: DryRunStatistics;
};

const STAT_GROUPS: Array<{ key: keyof Omit<DryRunStatistics, 'rows' | 'images'>; title: string }> = [
  { key: 'tags', title: 'Tags' },
  { key: 'robot_types', title: 'Robot types' },
  { key: 'robot_names', title: 'Robot names' },
  { key: 'img_widths', title: 'Image widths' },
  { key: 'img_heights', title: 'Image heights' },
  { key: 'species', title: 'Species' },
];

function asNumbers(value: unknown): number[] {
  if (Array.isArray(value)) return value.map(Number).filter(item => Number.isFinite(item));
  const number = Number(value);
  return Number.isFinite(number) ? [number] : [];
}

function boxLabels(raw: string): GroundTruthLabel[] {
  if (!raw || raw === '{}' || raw === 'null') return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
  const labels: GroundTruthLabel[] = [];
  Object.entries(parsed as Record<string, { x?: unknown; y?: unknown; width?: unknown; height?: unknown }>).forEach(
    ([name, coords]) => {
      if (!coords || typeof coords !== 'object') return;
      const xs = asNumbers(coords.x);
      const ys = asNumbers(coords.y);
      const widths = asNumbers(coords.width);
      const heights = asNumbers(coords.height);
      const count = Math.min(xs.length, ys.length, widths.length, heights.length);
      for (let index = 0; index < count; index += 1) {
        labels.push({
          class_name: name,
          type: 'bbox',
          bbox: {
            x1: xs[index],
            y1: ys[index],
            x2: xs[index] + widths[index],
            y2: ys[index] + heights[index],
          },
        });
      }
    },
  );
  return labels;
}

function classificationText(raw: string): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw) as { classifications?: unknown };
    if (!Array.isArray(parsed.classifications)) return '';
    return parsed.classifications.map(item => String(item)).filter(Boolean).join(', ');
  } catch {
    return '';
  }
}

function downloadCsv(csv: string, datasetId?: string) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${(datasetId || 'dry-run').replace('@', '-')}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function LabeledImage({ src, labels }: { src: string; labels: GroundTruthLabel[] }) {
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const paint = () => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas || img.naturalWidth === 0) return;
    canvas.width = img.offsetWidth;
    canvas.height = img.offsetHeight;
    canvas.dataset.imageWidth = String(img.naturalWidth);
    canvas.dataset.imageHeight = String(img.naturalHeight);
    canvas.dataset.displayWidth = String(img.offsetWidth);
    canvas.dataset.displayHeight = String(img.offsetHeight);
    drawBboxLabelsOnCanvas(canvas, labels, { showLabels: true });
  };

  return (
    <div style={{ position: 'relative', width: 240 }}>
      <img ref={imgRef} src={src} alt="" onLoad={paint} style={{ width: '100%', display: 'block' }} />
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      />
    </div>
  );
}

function StatChart({ title, rows }: { title: string; rows: CountRow[] }) {
  const data = rows.slice(0, 8);
  const pie = data.length > 0 && data.length <= 6;
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 6 }}>{title}</div>
      {data.length === 0 ? (
        <div style={{ color: tokens.textMuted, fontSize: '0.82rem' }}>None</div>
      ) : (
        <div style={{ width: '100%', height: pie ? 210 : Math.max(180, data.length * 28) }}>
          <ResponsiveContainer width="100%" height="100%">
            {pie ? (
              <PieChart>
                <Pie data={data} dataKey="count" nameKey="value" outerRadius={70} stroke={tokens.surface}>
                  {data.map((row, index) => (
                    <Cell key={row.value} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            ) : (
              <BarChart data={data} layout="vertical" margin={{ left: 8, right: 12, top: 4, bottom: 4 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="value" width={108} tick={{ fontSize: 11, fill: tokens.textMuted }} />
                <Tooltip />
                <Bar dataKey="count" fill={tokens.primary} radius={2} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function HorizontalTable({ columns, rows }: { columns: string[]; rows: Array<Record<string, string>> }) {
  const topRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return undefined;
    const measure = () => setWidth(table.scrollWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(table);
    return () => observer.disconnect();
  }, [columns, rows]);

  return (
    <div>
      <div
        ref={topRef}
        onScroll={() => {
          if (bodyRef.current && topRef.current && bodyRef.current.scrollLeft !== topRef.current.scrollLeft) {
            bodyRef.current.scrollLeft = topRef.current.scrollLeft;
          }
        }}
        style={{ overflowX: 'scroll', overflowY: 'hidden', height: 14 }}
      >
        <div style={{ width, height: 1 }} />
      </div>
      <div
        ref={bodyRef}
        onScroll={() => {
          if (bodyRef.current && topRef.current && topRef.current.scrollLeft !== bodyRef.current.scrollLeft) {
            topRef.current.scrollLeft = bodyRef.current.scrollLeft;
          }
        }}
        style={{ overflowX: 'auto' }}
      >
        <table
          ref={tableRef}
          style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}
        >
          <thead>
            <tr>
              {columns.map(column => (
                <th
                  key={column}
                  style={{
                    textAlign: 'left',
                    padding: '0.35rem 0.5rem',
                    borderBottom: `1px solid ${tokens.border}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                {columns.map(column => (
                  <td
                    key={column}
                    style={{
                      padding: '0.35rem 0.5rem',
                      borderBottom: `1px solid ${tokens.border}`,
                      whiteSpace: 'nowrap',
                      maxWidth: 360,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {row[column] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function DryRunPreview({ dryRun }: { dryRun: DryRunPreviewData | null }) {
  const [showVisuals, setShowVisuals] = useState(false);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [visualError, setVisualError] = useState<string | null>(null);
  const previewRows = dryRun?.preview ?? [];
  const columns = dryRun?.columns ?? (previewRows[0] ? Object.keys(previewRows[0]) : []);

  useEffect(() => {
    const rows = dryRun?.preview ?? [];
    if (!showVisuals || rows.length === 0) return undefined;
    const locations = Array.from(
      new Set(rows.map(row => row.file_location).filter(location => location && location.startsWith('s3://'))),
    );
    if (locations.length === 0) {
      setImageUrls({});
      return undefined;
    }
    let cancelled = false;
    setVisualError(null);
    api.dataviewer
      .getPresignedUrls(locations)
      .then(response => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        response.data.urls.forEach(item => {
          if (item.presigned_url) next[item.s3_url] = item.presigned_url;
        });
        setImageUrls(next);
      })
      .catch((err: { response?: { data?: { detail?: string } }; message?: string }) => {
        if (!cancelled) setVisualError(err?.response?.data?.detail || err?.message || 'Could not load images');
      });
    return () => {
      cancelled = true;
    };
  }, [showVisuals, dryRun?.preview]);

  return (
    <>
      <Card
        title="Dry-run preview"
        actions={
          dryRun ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={showVisuals}
                  onChange={event => setShowVisuals(event.target.checked)}
                />
                Preview images and labels
              </label>
              <Button
                disabled={!dryRun.csv_text}
                onClick={() => dryRun.csv_text && downloadCsv(dryRun.csv_text, dryRun.dataset_id)}
              >
                Download CSV
              </Button>
            </div>
          ) : null
        }
      >
        {!dryRun ? (
          <EmptyState>Run dry-run to preview matching rows.</EmptyState>
        ) : previewRows.length === 0 ? (
          <EmptyState>Query returned no rows.</EmptyState>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ color: tokens.textMuted, fontSize: '0.85rem' }}>
              {dryRun.num_records ?? previewRows.length} rows. Showing {previewRows.length}.
              {dryRun.statistics
                ? ` ${dryRun.statistics.images} images across ${dryRun.statistics.rows} rows.`
                : ''}
            </div>
            {dryRun.statistics && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: '1rem',
                }}
              >
                {STAT_GROUPS.map(group => (
                  <StatChart key={group.key} title={group.title} rows={dryRun.statistics?.[group.key] ?? []} />
                ))}
              </div>
            )}
            <HorizontalTable columns={columns} rows={previewRows} />
            {showVisuals && (
              <div style={{ display: 'grid', gap: 12 }}>
                {visualError && <div style={{ color: tokens.danger, fontSize: '0.85rem' }}>{visualError}</div>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {previewRows.map((row, index) => {
                    const location = row.file_location || '';
                    const src = imageUrls[location];
                    const labels = boxLabels(row.bboxes || '');
                    const classes = classificationText(row.classifications || '');
                    return (
                      <div key={`${location}-${index}`} style={{ width: 240 }}>
                        {src ? (
                          <LabeledImage src={src} labels={labels} />
                        ) : (
                          <div
                            style={{
                              height: 140,
                              display: 'grid',
                              placeItems: 'center',
                              color: tokens.textMuted,
                              background: tokens.surfaceMuted,
                              fontSize: '0.8rem',
                            }}
                          >
                            {location.startsWith('s3://') && !visualError ? 'Loading image' : 'No image'}
                          </div>
                        )}
                        <div style={{ marginTop: 4, fontSize: '0.75rem', color: tokens.textMuted }}>
                          {labels.length} boxes{classes ? ` · ${classes}` : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </>
  );
}
